import { useEffect, useState } from "react";
import { ListChecks, Plus, Trash2, Pencil, Sparkles, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Schemes() {
  const [schemes, setSchemes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [schemeForm, setSchemeForm] = useState({ sigla: "", descrizione: "" });
  const [schemeOpen, setSchemeOpen] = useState(false);
  const [editScheme, setEditScheme] = useState(null);
  const [reqForm, setReqForm] = useState({ codice: "", titolo: "", descrizione_requisito: "" });
  const [reqOpen, setReqOpen] = useState(false);
  const [editReq, setEditReq] = useState(null);
  const [extracting, setExtracting] = useState(false);

  const loadReqs = () => api.get(`/requirements?scheme_id=${selected.id}`).then((r) => setReqs(r.data));

  const loadSchemes = () => api.get("/schemes").then((r) => {
    setSchemes(r.data);
    if (!selected && r.data.length) setSelected(r.data[0]);
  });
  useEffect(() => { loadSchemes(); }, []);
  useEffect(() => { if (selected) api.get(`/requirements?scheme_id=${selected.id}`).then((r) => setReqs(r.data)); }, [selected]);

  const saveScheme = async () => {
    try {
      if (editScheme) await api.put(`/schemes/${editScheme.id}`, schemeForm);
      else await api.post("/schemes", schemeForm);
      toast.success("Schema salvato"); setSchemeOpen(false); loadSchemes();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delScheme = async (s) => {
    if (!window.confirm(`Eliminare lo schema ${s.sigla} e i suoi requisiti?`)) return;
    try {
      await api.delete(`/schemes/${s.id}`);
      if (selected?.id === s.id) setSelected(null);
      toast.success("Schema eliminato");
      loadSchemes();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveReq = async () => {
    try {
      const payload = { ...reqForm, scheme_id: selected.id };
      if (editReq) await api.put(`/requirements/${editReq.id}`, payload);
      else await api.post("/requirements", payload);
      toast.success("Requisito salvato"); setReqOpen(false);
      api.get(`/requirements?scheme_id=${selected.id}`).then((r) => setReqs(r.data));
    } catch (e) { toast.error(apiError(e)); }
  };
  const delReq = async (id) => { await api.delete(`/requirements/${id}`); api.get(`/requirements?scheme_id=${selected.id}`).then((r) => setReqs(r.data)); };

  const extractReqs = async () => {
    if (!selected) return;
    setExtracting(true);
    const schemeId = selected.id;
    try {
      await api.post(`/schemes/${schemeId}/extract-requirements`);
      toast.info("Estrazione avviata. Analisi della fonte normativa in corso…");
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`/schemes/${schemeId}/extraction-status`);
          if (s.data.status === "done") {
            clearInterval(poll);
            setExtracting(false);
            if (s.data.created > 0) toast.success(`${s.data.created} requisiti estratti (${s.data.found} trovati nella norma)`);
            else toast.info(`Nessun nuovo requisito da aggiungere (${s.data.found} trovati)`);
            if (selected?.id === schemeId) loadReqs();
          } else if (s.data.status === "error") {
            clearInterval(poll);
            setExtracting(false);
            toast.error("Estrazione non riuscita. Riprova.");
          }
        } catch (e) { /* keep polling */ }
      }, 4000);
    } catch (e) { setExtracting(false); toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHero label="Registro globale" title="Schemi & Requisiti">
        <Button onClick={() => { setEditScheme(null); setSchemeForm({ sigla: "", descrizione: "" }); setSchemeOpen(true); }}
          data-testid="new-scheme-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
          <Plus className="h-4 w-4 mr-2" /> Nuovo schema
        </Button>
      </PageHero>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          {schemes.map((s) => (
            <Card key={s.id} data-testid={`scheme-${s.id}`}
              onClick={() => setSelected(s)}
              className={`p-4 cursor-pointer border transition-colors ${selected?.id === s.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-semibold">{s.sigla}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.descrizione}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setEditScheme(s); setSchemeForm({ sigla: s.sigla, descrizione: s.descrizione || "" }); setSchemeOpen(true); }}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={(e) => { e.stopPropagation(); delScheme(s); }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
          {schemes.length === 0 && <p className="text-sm text-slate-500">Nessuno schema. Aggiungine uno (es. ISO 9001).</p>}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <Card className="border-slate-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-5 border-b border-slate-200">
                <h2 className="font-display text-lg font-medium">Requisiti · {selected.sigla}</h2>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={extractReqs} disabled={extracting}
                    data-testid="extract-reqs-btn" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                    {extracting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                    {extracting ? "Estrazione in corso…" : "Estrai da Fonte Normativa"}
                  </Button>
                  <Button size="sm" onClick={() => { setEditReq(null); setReqForm({ codice: "", titolo: "", descrizione_requisito: "" }); setReqOpen(true); }}
                    data-testid="new-req-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                    <Plus className="h-4 w-4 mr-1" /> Requisito
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-slate-100">
                {reqs.map((r) => (
                  <div key={r.id} data-testid={`req-${r.id}`} className="flex items-start justify-between p-4 hover:bg-slate-50">
                    <div className="flex gap-3">
                      <Badge variant="outline" className="font-mono shrink-0">{r.codice}</Badge>
                      <div>
                        <div className="text-sm font-medium">{r.titolo}</div>
                        <div className="text-xs text-slate-500">{r.descrizione_requisito}</div>
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditReq(r); setReqForm({ codice: r.codice, titolo: r.titolo, descrizione_requisito: r.descrizione_requisito || "" }); setReqOpen(true); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => delReq(r.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
                {reqs.length === 0 && <p className="text-sm text-slate-500 p-6">Nessun requisito registrato per questo schema.</p>}
              </div>
            </Card>
          ) : (
            <Card className="p-12 text-center border-dashed">
              <ListChecks className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Seleziona uno schema per gestirne i requisiti.</p>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={schemeOpen} onOpenChange={setSchemeOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editScheme ? "Modifica schema" : "Nuovo schema"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Sigla</Label><Input placeholder="ISO 9001" value={schemeForm.sigla} data-testid="scheme-sigla" onChange={(e) => setSchemeForm({ ...schemeForm, sigla: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Descrizione</Label><Textarea value={schemeForm.descrizione} onChange={(e) => setSchemeForm({ ...schemeForm, descrizione: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchemeOpen(false)}>Annulla</Button>
            <Button onClick={saveScheme} data-testid="save-scheme-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editReq ? "Modifica requisito" : "Nuovo requisito"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Codice</Label><Input placeholder="7.5" value={reqForm.codice} data-testid="req-codice" onChange={(e) => setReqForm({ ...reqForm, codice: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Titolo</Label><Input value={reqForm.titolo} data-testid="req-titolo" onChange={(e) => setReqForm({ ...reqForm, titolo: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Descrizione</Label><Textarea value={reqForm.descrizione_requisito} onChange={(e) => setReqForm({ ...reqForm, descrizione_requisito: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReqOpen(false)}>Annulla</Button>
            <Button onClick={saveReq} data-testid="save-req-btn" className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
